import { SelectNameValue } from 'business/models/CustomModels';
import {
  EICheckBoxVertical,
  EISelectBoxVertical,
} from 'components/customComponents/Layers/EICheckBox';
import LayersHeading from 'components/customComponents/Layers/LayersHeading';
import ELayersView, { EGridVaryAlong } from 'enums/ELayersView';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FmlxButton } from 'fmlx-common-ui';
import { usePreserveExperiment, useRMDispatch, useRMSelector } from 'redux/hooks';
import { ProteinLayerDTO } from 'business/models/Models';
import {
  GetProteinFormulationName,
  LayerDesignTabs,
  showAlertSnackBar,
} from 'utils/Global';
import LayersError from 'components/customComponents/Layers/LayersError';
import { getUniqueProteinLayerName } from 'business/plateviewer/GenerateUtil';
import { selectExperimentLayers } from '../../../redux/selectors/ExperimentStateSelector';
import { updateExperimentLayers } from 'redux/reducers/experimentDataReducer';
import { cloneDeep } from 'lodash';
import { withTabBackButton, WithTabBackButtonStateProps } from 'components/tab';
import { InputWithUnit } from 'components/input/';
import { useCanvasPreference } from 'redux/hooks/common';
import { ECanvasPreferencesKeys } from 'enums/ECanvasPreferencesKeys';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';

interface EditProteinProps extends WithTabBackButtonStateProps {
  setContentView: (contentView: ELayersView) => void;
  containerMaxDropVolume: number;
  handleBackToPreviousScreen: (contentView: ELayersView) => () => void;
  selectedProtein: ProteinLayerDTO;
  setSelectedTab: (val: number) => void;
  shouldReturnToLayersExplorer?: boolean;
  onApplyComplete?: () => void;
  selectedProteinDeleted?: boolean;
}
function EditProtein(props: EditProteinProps) {
  const {
    setContentView,
    containerMaxDropVolume,
    handleBackToPreviousScreen,
    setHandleOnTabBackPress,
    selectedProtein,
    setSelectedTab,
    shouldReturnToLayersExplorer = false,
    onApplyComplete,
    selectedProteinDeleted = false,
  } = props;
  const proteinVaryAlong: SelectNameValue[] = [
    { name: 'None', value: EGridVaryAlong.NONE },
    { name: 'Rows', value: EGridVaryAlong.ROW },
    { name: 'Columns', value: EGridVaryAlong.COLUMN },
    { name: 'Well Numbers', value: EGridVaryAlong.WELLNUMBER },
  ];

  const dispatch = useRMDispatch();
  const [error, setError] = useState<boolean>(false);
  const [highVolError, setHighVolError] = useState<boolean>(false);
  const [wellStartError, setwellStartError] = useState<boolean>(false);
  const [wellStopError, setwellStopError] = useState<boolean>(false);
  const _layers = useRMSelector(selectExperimentLayers);
  const _setLayers = useCallback(
    (lyrs) => dispatch(updateExperimentLayers(lyrs)),
    [dispatch],
  );
  const callPreserveUndoableState = usePreserveExperiment();

  const layerName: string = useMemo(() => {
    return GetProteinFormulationName(
      !selectedProtein
        ? null
        : selectedProtein.proteinLayerProteinFormulation[0].proteinFormulationId,
    );
  }, [selectedProtein]);

  const [concentrationVaryAlong, setConcentrationVaryAlong] = useState(
    !selectedProtein
      ? null
      : selectedProtein.proteinLayerProteinFormulation[0].proteinDropVolumeVaryAlong,
  );

  const [startConcentration, setStartConcentration] = useState<string | null>(() => {
    if (!selectedProtein) {
      return null;
    }
    const pf = selectedProtein.proteinLayerProteinFormulation[0];

    if (pf.startProteinDropVolume === null && pf.proteinFormulation) {
      const proteinInfo = pf.proteinFormulation as any;
      if (proteinInfo.proteinVolume !== undefined) {
        return proteinInfo.proteinVolume.toString();
      }
    }
    return pf.startProteinDropVolume.toString();
  });

  const [stopConcentration, setStopConcentration] = useState<string | null>(() => {
    if (!selectedProtein) {
      return null;
    }
    const pf = selectedProtein.proteinLayerProteinFormulation[0];

    if (pf.stopProteinDropVolume === null && pf.proteinFormulation) {
      const proteinInfo = pf.proteinFormulation as any;
      if (proteinInfo.proteinVolume !== undefined) {
        return proteinInfo.proteinVolume.toString();
      }
    }
    return pf.stopProteinDropVolume.toString();
  });

  const [wellConcentrationVaryAlong, setWellConcentrationVaryAlong] = useState(
    !selectedProtein
      ? null
      : selectedProtein.proteinLayerProteinFormulation[0].wellDropVolumeVaryAlong,
  );

  const [wellStartConcentration, setWellStartConcentration] = useState<string | null>(
    () => {
      if (!selectedProtein) {
        return null;
      }
      const pf = selectedProtein.proteinLayerProteinFormulation[0];

      if (pf.startWellDropVolume === null && pf.proteinFormulation) {
        const proteinInfo = pf.proteinFormulation as any;
        if (proteinInfo.dropVolume !== undefined) {
          return proteinInfo.dropVolume.toString();
        }
      }
      return pf.startWellDropVolume.toString();
    },
  );

  const [wellStopConcentration, setWellStopConcentration] = useState<string | null>(
    () => {
      if (!selectedProtein) {
        return null;
      }
      const pf = selectedProtein.proteinLayerProteinFormulation[0];

      if (pf.stopWellDropVolume === null && pf.proteinFormulation) {
        const proteinInfo = pf.proteinFormulation as any;
        if (proteinInfo.dropVolume !== undefined) {
          return proteinInfo.dropVolume.toString();
        }
      }
      return pf.stopWellDropVolume.toString();
    },
  );

  const nameProteinLayerUsingPFName = useCanvasPreference(
    ECanvasPreferencesKeys.NameProteinLayerUsingPFName,
  );

  function changeConcentrationVaryAlong(e) {
    setConcentrationVaryAlong(Number(e.target.value));
  }

  function handleStartConcentration(e) {
    if (isNaN(e.target.value)) {
      return;
    }
    setStartConcentration(e.target.value);
    if (Number(e.target.value) > containerMaxDropVolume || e.target.value === '') {
      setError(true);
    } else {
      setError(false);
    }
  }

  function handleStopConcentration(e) {
    if (isNaN(e.target.value)) {
      return;
    }
    setStopConcentration(e.target.value);
    if (Number(e.target.value) > containerMaxDropVolume || e.target.value === '') {
      setHighVolError(true);
    } else {
      setHighVolError(false);
    }
  }

  function wellChangeConcentrationVaryAlong(e) {
    setWellConcentrationVaryAlong(Number(e.target.value));
  }

  function wellHandleStartConcentration(e) {
    if (isNaN(e.target.value)) {
      return;
    }
    setWellStartConcentration(e.target.value);
    if (Number(e.target.value) > containerMaxDropVolume || e.target.value === '') {
      setwellStartError(true);
    } else {
      setwellStartError(false);
    }
  }

  function wellHandleStopConcentration(e) {
    if (isNaN(e.target.value)) {
      return;
    }
    setWellStopConcentration(e.target.value);
    if (Number(e.target.value) > containerMaxDropVolume || e.target.value === '') {
      setwellStopError(true);
    } else {
      setwellStopError(false);
    }
  }

  function applyProtein() {
    if (selectedProteinDeleted) {
      showAlertSnackBar(
        EAlertType.WARNING,
        ESnackBarPosition.TOP,
        'Selected protein has been deleted.',
      );
      return;
    }

    callPreserveUndoableState();
    const selectedLayerDetail: ProteinLayerDTO = cloneDeep(selectedProtein);

    selectedLayerDetail.proteinLayerProteinFormulation[0].proteinDropVolumeVaryAlong =
      concentrationVaryAlong;
    selectedLayerDetail.proteinLayerProteinFormulation[0].startProteinDropVolume =
      parseFloat(startConcentration);
    selectedLayerDetail.proteinLayerProteinFormulation[0].stopProteinDropVolume =
      parseFloat(stopConcentration);
    selectedLayerDetail.proteinLayerProteinFormulation[0].startWellDropVolume =
      parseFloat(wellStartConcentration);
    selectedLayerDetail.proteinLayerProteinFormulation[0].stopWellDropVolume =
      parseFloat(wellStopConcentration);
    selectedLayerDetail.proteinLayerProteinFormulation[0].wellDropVolumeVaryAlong =
      wellConcentrationVaryAlong;
    const name = GetProteinFormulationName(
      !selectedLayerDetail
        ? null
        : selectedProtein.proteinLayerProteinFormulation[0].proteinFormulationId,
    );

    const tempLayers = cloneDeep(_layers);
    const index = tempLayers.findIndex((l) => l.id == selectedProtein.layerId);
    tempLayers[index].name = getUniqueProteinLayerName(
      _layers,
      name,
      nameProteinLayerUsingPFName,
      tempLayers[index].id,
    );
    tempLayers[index].dirty = true;
    tempLayers[index].layerDetail = selectedLayerDetail;
    _setLayers(tempLayers);
    setContentView(ELayersView.LAYERDESIGNVIEW);
    if (setSelectedTab) {
      setSelectedTab(LayerDesignTabs.Layers);
    }
    handleBackToPreviousScreen(null);
    setHandleOnTabBackPress(null);

    if (shouldReturnToLayersExplorer && onApplyComplete) {
      onApplyComplete();
    }
  }

  function selectProtein() {
    setContentView(ELayersView.SELECTPROTEIN);
  }

  useEffect(() => {
    setHandleOnTabBackPress(() =>
      handleBackToPreviousScreen(ELayersView.LAYERDESIGNVIEW),
    );
  }, [setHandleOnTabBackPress]);
  return (
    <div
      id="commandpanel-proteinlayer-edit-wrapper"
      className="flex flex-column flex-x-center"
      style={{ padding: 24 }}
    >
      <EICheckBoxVertical
        labelText="Protein Name"
        textBoxText={layerName}
        onClick={() => selectProtein()}
      />

      <LayersHeading text="Protein Volume" className="mx-0" />

      <EISelectBoxVertical
        labelText="Vary Along"
        selectedValue={concentrationVaryAlong}
        onChange={changeConcentrationVaryAlong}
        options={proteinVaryAlong}
      />
      <div
        id="commandpanel-proteinlayer-edit-proteinvolume-inputgroup"
        className="w-100pr flex flex-space-between"
      >
        <InputWithUnit
          unit="µL"
          labelText="Start"
          inputValue={startConcentration}
          onChange={handleStartConcentration}
          error={error}
        />
        {Number(concentrationVaryAlong) !== EGridVaryAlong.NONE && (
          <InputWithUnit
            unit="µL"
            labelText="Stop"
            inputValue={stopConcentration}
            onChange={handleStopConcentration}
            error={highVolError}
            isDisabled={concentrationVaryAlong === EGridVaryAlong.NONE}
          />
        )}
      </div>

      <LayersHeading text="Well Solution Volume" className="mx-0" />

      <EISelectBoxVertical
        labelText="Vary Along"
        selectedValue={wellConcentrationVaryAlong}
        onChange={wellChangeConcentrationVaryAlong}
        options={proteinVaryAlong}
      />
      <div
        id="commandpanel-proteinlayer-edit-wellsolution-inputgroup"
        className="w-100pr flex flex-space-between"
      >
        <InputWithUnit
          unit="µL"
          labelText="Start"
          inputValue={wellStartConcentration}
          onChange={wellHandleStartConcentration}
          error={wellStartError}
        />
        {Number(wellConcentrationVaryAlong) !== EGridVaryAlong.NONE && (
          <InputWithUnit
            unit="µL"
            labelText="Stop"
            inputValue={wellStopConcentration}
            onChange={wellHandleStopConcentration}
            error={wellStopError}
            isDisabled={wellConcentrationVaryAlong === EGridVaryAlong.NONE}
          />
        )}
      </div>
      <div
        id="commandpanel-proteinlayer-edit-footer"
        className="flex"
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '5px',
        }}
      >
        {(error || wellStartError || wellStopError || highVolError) && (
          <LayersError
            errorText="Some of the data you entered is invalid"
            style={{ marginTop: '1rem' }}
          />
        )}
        <FmlxButton
          id="commandpanel-proteinlayer-edit-applybutton"
          style={{
            background: '#008F40',
            color: '#ffffff',
            display: 'flex',
            justifyContent: 'flex-end',
            minHeight: '38px',
            maxHeight: '38px',
            height: '38px',
          }}
          type={FmlxButton.Type.POSITIVE}
          disabled={error || wellStartError || wellStopError || highVolError}
          onClick={applyProtein}
          label="Apply"
        />
      </div>
    </div>
  );
}

export default withTabBackButton(EditProtein);
